<template>
  <CModal
    :show.sync="show"
    @close="close"
    size="lg"
  >
    <template #header>
      <h5 class="modal-title">
        <translate translate-context="payout.invoiceUpload">Upload invoice</translate>
      </h5>
      <CButtonClose @click="close" />
    </template>

    <template #default>
      <CForm @submit.prevent="handleSubmit">
        <!-- Info importo -->
        <PAlert color="info" class="mb-4" icon="cipInfo">
          <div class="font-weight-semi-bold mb-1">
            <translate translate-context="payout.invoiceUpload">You need a valid invoice to receive payment</translate>
          </div>
          <div class="small">
            <translate translate-context="payout.invoiceUpload">
              The invoice must be in PDF format and contain all tax details required for payment.
            </translate>
          </div>
        </PAlert>

        <!-- Riepilogo -->
        <div class="mb-4" v-if="payout">
          <h6 class="text-muted text-uppercase small font-weight-bold mb-3">
            <translate translate-context="payout.invoiceUpload">Riepilogo</translate>
          </h6>
          <div class="invoice-summary">
            <div class="d-flex justify-content-between py-2 border-bottom">
              <span class="text-muted">
                <translate translate-context="payout.invoiceUpload">Importo netto da fatturare:</translate>
              </span>
              <span class="font-weight-bold text-primary">
                {{ payout.paid_net | currency(payout.currency || currencyCodes.EUR) }}
              </span>
            </div>
            <div class="d-flex justify-content-between py-2 border-bottom" v-if="payout.vat_amount !== undefined">
              <span class="text-muted">
                <translate translate-context="payout.invoiceUpload">Regime IVA:</translate>
              </span>
              <span class="font-weight-bold">
                {{ payout.vat_amount | currency(payout.currency || currencyCodes.EUR) }}
              </span>
            </div>
            <div class="d-flex justify-content-between py-2" v-if="payout.last_payment_date">
              <span class="text-muted">
                <translate translate-context="payout.invoiceUpload">Ultimo pagamento:</translate>
              </span>
              <span class="font-weight-bold">
                {{ payout.last_payment_date | formatDate }}
              </span>
            </div>
          </div>
        </div>

        <!-- Dati di fatturazione -->
        <div class="mb-4" v-if="payout">
          <p class="small text-muted mb-2">
            <translate translate-context="payout.invoiceUpload">
              Per favore includi i seguenti dati di fatturazione nella tua fattura:
            </translate>
          </p>
          <div class="billing-info-box">
            <div class="billing-info-content">
              <div class="font-italic text-muted small">
                <div>Pica Group S.p.A.</div>
                <div>Via dell'Aprica, 12 - 20158 Milano</div>
                <div>PI: 02529950392</div>
                <div>SDI: M5UXCR1</div>
              </div>
            </div>
            <CButton
              color="primary"
              variant="ghost"
              size="sm"
              class="billing-info-copy"
              @click="copyBillingInfo"
            >
              <translate translate-context="payout.invoiceUpload">Copia</translate>
            </CButton>
          </div>
          <p class="small text-muted mt-2 mb-0">
            <translate translate-context="payout.invoiceUpload">
              Copia e incolla questi dati nella tua fattura per garantire una corretta elaborazione.
            </translate>
          </p>
        </div>

        <!-- Invoice upload -->
        <div class="form-group">
          <label class="required" v-translate translate-context="payout.invoiceUpload">Invoice</label>
          <div v-if="!form.invoice" class="custom-file-upload" :class="{'is-invalid': validation.invoice === false}">
            <input
              type="file"
              ref="fileInput"
              @change="handleFileChange"
              accept=".pdf"
              class="d-none"
            />
            <div
              class="upload-area text-center py-4"
              @click="$refs.fileInput.click()"
              @dragover.prevent="onDragOver"
              @dragleave.prevent="onDragLeave"
              @drop.prevent="onDrop"
              :class="{ 'drag-over': isDragging }"
            >
              <CIcon name="cipUpload02" size="custom-size" :height="30" class="text-primary mb-2 d-block mx-auto" />
              <div>
                <translate translate-context="payout.invoiceUpload">Drag file here or</translate>&nbsp;
                <a href="#" @click.prevent="$refs.fileInput.click()" class="text-primary">
                  <translate translate-context="payout.invoiceUpload">click to upload</translate>
                </a>
              </div>
              <small class="text-muted">
                <translate translate-context="payout.invoiceUpload">(Only PDF, max size 10MB)</translate>
              </small>
            </div>
          </div>

          <div v-else class="uploaded-file">
            <CCard>
              <CCardBody class="py-2 px-3 d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <CIcon name="cipFile03" size="lg" class="mr-2 text-primary" />
                  <span>{{ form.invoice.name }}</span>
                </div>
                <CButton
                  color="danger"
                  size="sm"
                  variant="ghost"
                  @click="removeFile"
                >
                  <CIcon name="cipClose" size="sm" />
                </CButton>
              </CCardBody>
            </CCard>
          </div>
          <div v-if="validation.invoice === false" class="invalid-feedback d-block">
            <translate translate-context="payout.invoiceUpload">Invoice is required</translate>
          </div>
        </div>
      </CForm>
    </template>

    <template #footer>
      <CButton color="secondary" variant="outline" @click="close">
        <translate translate-context="payout.invoiceUpload">Cancel</translate>
      </CButton>
      <CButton
        color="primary"
        @click="handleSubmit"
        :disabled="!form.invoice || isUploading"
      >
        <CSpinner v-if="isUploading" size="sm" class="mr-2" />
        <translate translate-context="payout.invoiceUpload">Upload</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import PAlert from "@/components/PAlert.vue";
import {currencyCodes} from "../../../constants";

export default {
  name: 'InvoiceUploadModal',
  computed: {
    currencyCodes() {
      return currencyCodes
    }
  },
  components: {PAlert},
  props: {
    isUploading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      show: false,
      payout: null,
      isDragging: false,
      form: {
        invoice: null
      },
      validation: {
        invoice: null
      }
    }
  },
  methods: {
    open(payout) {
      this.payout = payout
      this.resetForm()
      this.show = true
    },

    close() {
      this.show = false
      this.payout = null
    },

    resetForm() {
      this.form = { invoice: null }
      this.validation = { invoice: null }
      this.isDragging = false
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    handleFileChange(event) {
      const file = event.target.files[0]
      this.processFile(file)
    },

    onDragOver() {
      this.isDragging = true
    },

    onDragLeave() {
      this.isDragging = false
    },

    onDrop(event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      this.processFile(file)
    },

    processFile(file) {
      if (!file) return

      const maxSize = 10 * 1024 * 1024
      if (file.size > maxSize) {
        this.notifyError(this.$pgettext('payout.invoiceUpload', 'Max file size 10MB'))
        return
      }

      if (file.type !== 'application/pdf') {
        this.notifyError(this.$pgettext('payout.invoiceUpload', 'Only PDF files are accepted'))
        return
      }

      this.form.invoice = file
      this.validation.invoice = true
    },

    removeFile() {
      this.form.invoice = null
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    copyBillingInfo() {
      const text = 'Pica Group S.p.A.\nVia dell\'Aprica, 12 - 20158 Milano\nPI: 02529950392\nSDI: M5UXCR1'

      navigator.clipboard.writeText(text).then(() => {
        this.notifySuccess(this.$pgettext('payout.invoiceUpload', 'Dati copiati negli appunti'))
      })
    },

    handleSubmit() {
      if (!this.form.invoice) {
        this.validation.invoice = false
        return
      }
      if (!this.payout) return

      this.$emit('submit', {
        payoutId: this.payout.id,
        invoice: this.form.invoice
      })
    }
  }
}
</script>

<style scoped>
.invoice-summary {
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  padding: 0.25rem 1rem;
}

.billing-info-box {
  position: relative;
  background-color: #f8f9fa;
  border: 1px solid #e4e7ea;
  border-radius: 0.375rem;
  padding: 1rem;
  padding-right: 4rem;
}

.billing-info-copy {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
}
</style>
